import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { i18n } from "@/i18n";
import { WidgetPublish } from "@/components/widget-publish";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useWidgetStore } from "@/stores/widget-store";

const IS_MAC: boolean =
  typeof navigator !== 'undefined' &&
  ((navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData?.platform?.toLowerCase().includes('mac') ??
   /mac/i.test(navigator.platform));

const MODIFIER_LABELS: Record<string, string> = {
  alt:   IS_MAC ? 'Option' : 'Alt',
  shift: 'Shift',
  ctrl:  'Ctrl',
};

function formatShortcut(shortcut: string): string {
  const parts = shortcut.toLowerCase().split('+');
  const key = parts[parts.length - 1].toUpperCase();
  const modifiers = parts.slice(0, -1).map(m => MODIFIER_LABELS[m] ?? m);
  return [...modifiers, key].join('+');
}

export const SettingsTab = () => {
  const { config, setStatus, setKeyboard } = useWidgetStore();
  const keyboard = config.keyboard ?? { enabled: false, shortcut: 'alt+a' };
  const effectiveShortcut = formatShortcut(window.cyA11yGlobals?.keyboardShortcut ?? 'alt+a');

  return (
    <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8 cy:pt-8">
      <Card className="cy:rounded-lg cy:gap-0 cy:shadow-none!">
        <CardHeader className="cy:border-b cy:border-gray-200 cy:pb-6">
          <CardTitle className="cy:text-xl">{i18n.__( 'General settings', 'accessibility-widget' )}</CardTitle>
          <CardDescription>
            {i18n.__( "Control your widget's visibility on desktop and mobile devices.", 'accessibility-widget' )}
          </CardDescription>
          <CardAction>
            <WidgetPublish />{" "}
          </CardAction>
        </CardHeader>
        <CardContent className="cy:p-0">
          <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8">
            <div className="cy:flex cy:flex-col cy:gap-4 cy:border-b cy:border-gray-200 cy:py-6">
              <div className="cy:flex cy:items-center cy:justify-between cy:space-x-2">
                <Label htmlFor="cy-toggle-widget-desktop" className="cy:text-base! cy:font-normal! cy:text-foreground!">{i18n.__( 'Enable widget on desktop', 'accessibility-widget' )}</Label>
                <Switch id="cy-toggle-widget-desktop" checked={config.status.desktop} onCheckedChange={(checked) => setStatus({ ...config.status, desktop: checked })} />
              </div>
            </div>
            <div className="cy:flex cy:flex-col cy:gap-4 cy:border-b cy:border-gray-200 cy:py-6">
              <div className="cy:flex cy:items-center cy:justify-between cy:space-x-2">
                <Label htmlFor="cy-toggle-widget-mobile" className="cy:text-base! cy:font-normal! cy:text-foreground!">{i18n.__( 'Enable widget on mobile layouts', 'accessibility-widget' )}</Label>
                <Switch id="cy-toggle-widget-mobile" checked={config.status.mobile} onCheckedChange={(checked) => setStatus({ ...config.status, mobile: checked })} />
              </div>
            </div>
            <div className="cy:flex cy:flex-col cy:gap-4 cy:py-6">
              <div className="cy:flex cy:items-center cy:justify-between cy:space-x-2">
                <Label htmlFor="cy-keyboard-shortcut-enable" className="cy:text-base! cy:font-normal! cy:text-foreground!">
                  {i18n.__( 'Enable keyboard shortcut', 'accessibility-widget' )}{' '}
                  <span className="cy:text-muted-foreground cy:font-normal">({effectiveShortcut})</span>
                </Label>
                <Switch
                  id="cy-keyboard-shortcut-enable"
                  checked={keyboard.enabled}
                  onCheckedChange={(checked) => setKeyboard({ ...keyboard, enabled: checked })}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
